// WCAG 2.1 contrast ratio per https://www.w3.org/TR/WCAG21/
//
// Contrast is advice here, never a gate: a brand's own colours are the
// organiser's call, so the server reports these pairs back on save and stores
// the theme either way. This copy exists so the admin UI can show the same
// verdict live, while the colour is being chosen, rather than after Save.
// backend/tests/contracts.test.ts asserts the two agree — the backend cannot
// import this package from source (see the Dockerfile note and perf.yml/zap.yml,
// which build the backend without building shared first).
export const WCAG_AA_RATIO = 4.5;
const HEX_RE = /^#([0-9a-f]{6})$/i;
const channelToLinear = (c) => {
    const s = c / 255;
    return s <= 0.03928 ? s / 12.92 : ((s + 0.055) / 1.055) ** 2.4;
};
export const parseHex = (hex) => {
    const m = HEX_RE.exec(hex);
    if (!m || !m[1])
        return null;
    const v = Number.parseInt(m[1], 16);
    return { r: (v >> 16) & 0xff, g: (v >> 8) & 0xff, b: v & 0xff };
};
export const relativeLuminance = (hex) => {
    const rgb = parseHex(hex);
    if (!rgb)
        throw new Error(`invalid hex color: ${hex}`);
    const r = channelToLinear(rgb.r);
    const g = channelToLinear(rgb.g);
    const b = channelToLinear(rgb.b);
    return 0.2126 * r + 0.7152 * g + 0.0722 * b;
};
export const contrastRatio = (a, b) => {
    const la = relativeLuminance(a);
    const lb = relativeLuminance(b);
    const [hi, lo] = la > lb ? [la, lb] : [lb, la];
    return (hi + 0.05) / (lo + 0.05);
};
export const meetsWcagAa = (foreground, background) => contrastRatio(foreground, background) >= WCAG_AA_RATIO;
export const CHECKED_CONTRAST_PAIRS = [
    { foreground: 'onSurface', background: 'surface', label: 'Body text on the surface color' },
    { foreground: 'onPrimary', background: 'primary', label: 'Text on the primary color' },
];
/**
 * Which checked pairs this palette would be flagged for. Advisory only — the
 * server stores whatever the organiser chooses, so a failure here is something
 * to tell them about, never something that blocks a save.
 *
 * Skips any pair whose colours are not yet complete hex codes, so a half-typed
 * field reports nothing rather than throwing.
 */
export const findContrastFailures = (tokens) => {
    const failures = [];
    for (const pair of CHECKED_CONTRAST_PAIRS) {
        const fg = tokens[pair.foreground];
        const bg = tokens[pair.background];
        if (!fg || !bg || !parseHex(fg) || !parseHex(bg))
            continue;
        const ratio = contrastRatio(fg, bg);
        if (ratio < WCAG_AA_RATIO) {
            failures.push({ ...pair, foregroundHex: fg, backgroundHex: bg, ratio });
        }
    }
    return failures;
};
/**
 * A failing ratio, rounded so it never rounds *up* to the limit it just missed.
 * 4.478:1 shown as "4.5:1, but you need at least 4.5:1" reads as a bug and
 * leaves the organiser nothing to act on.
 */
export const formatRatio = (ratio) => (Math.floor(ratio * 10) / 10).toFixed(1);
/**
 * One sentence about a pair, phrased as advice. Contrast is not enforced —
 * a brand's own colours are the organiser's call — so this reports what
 * attendees will experience instead of instructing them to change it.
 */
export const describeContrastFailure = (f) => `${f.label} is low contrast — ${f.foregroundHex} on ${f.backgroundHex} is ${formatRatio(f.ratio)}:1, below the ${WCAG_AA_RATIO}:1 WCAG AA asks for. Some attendees may find it hard to read.`;
//# sourceMappingURL=contrast.js.map